"use client";

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs";

import { cn } from "@/lib/utils";

type TabsVariant = "default" | "underline";

function Tabs({ className, ...props }: TabsPrimitive.Root.Props) {
    return (
        <TabsPrimitive.Root
            className={cn("flex flex-col data-[orientation=vertical]:flex-row", className)}
            data-slot="tabs"
            {...props}
        />
    );
}

function TabsList({
    variant = "default",
    className,
    children,
    ...props
}: TabsPrimitive.List.Props & {
    variant?: TabsVariant;
}) {
    return (
        <TabsPrimitive.List
            className={cn(
                "relative z-0 flex w-fit items-center justify-center gap-x-0.5 text-muted-foreground",
                "data-[orientation=vertical]:flex-col",
                variant === "default"
                    ? "rounded-lg bg-muted p-0.5 text-muted-foreground/64"
                    : "data-[orientation=vertical]:px-1 data-[orientation=horizontal]:py-1.5 *:data-[slot=tabs-trigger]:hover:bg-accent",
                className
            )}
            data-slot="tabs-list"
            {...props}
        >
            {children}
            <TabsPrimitive.Indicator
                className={cn(
                    "-translate-y-(--active-tab-bottom) absolute bottom-0 left-0 h-(--active-tab-height) w-(--active-tab-width) translate-x-(--active-tab-left) transition-[width,translate] duration-200 ease-in-out",
                    variant === "underline"
                        ? "data-[orientation=vertical]:-translate-x-px z-10 bg-primary data-[orientation=horizontal]:h-0.5 data-[orientation=vertical]:w-0.5 data-[orientation=horizontal]:translate-y-px"
                        : "-z-1 rounded-md bg-background shadow-sm dark:bg-accent"
                )}
                data-slot="tab-indicator"
            />
        </TabsPrimitive.List>
    );
}

function TabsTab({ className, ...props }: TabsPrimitive.Tab.Props) {
    return (
        <TabsPrimitive.Tab
            className={cn(
                "flex flex-1 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-transparent font-medium outline-none transition-[color,background-color,box-shadow] focus-visible:ring-2 focus-visible:ring-ring data-disabled:pointer-events-none data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                "text-orsane-muted hover:text-orsane-primary data-active:text-orsane-primary hover:bg-zinc-200/40",
                "gap-1.5 px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(2)-1px)]",
                "data-[orientation=vertical]:w-full data-[orientation=vertical]:justify-start",
                className
            )}
            data-slot="tabs-trigger"
            {...props}
        />
    );
}

function TabsPanel({ className, ...props }: TabsPrimitive.Panel.Props) {
    return <TabsPrimitive.Panel className={cn("flex-1 outline-none", className)} data-slot="tabs-content" {...props} />;
}

export { Tabs, TabsList, TabsTab, TabsTab as TabsTrigger, TabsPanel, TabsPanel as TabsContent };
